import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';
import ReportButton from '../components/ReportButton.jsx';
import { TOPIC_FILTER_OPTIONS, topicLabel } from '../constants/topics.js';

export default function Quiz() {
  const [topic, setTopic]           = useState('');
  const [questions, setQuestions]   = useState([]);
  const [idx, setIdx]               = useState(0);
  const [loading, setLoading]       = useState(true);
  const [chosen, setChosen]         = useState(null);
  const [result, setResult]         = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats]           = useState({ correct: 0, total: 0 });

  const load = useCallback((t) => {
    setLoading(true); setIdx(0); setChosen(null); setResult(null);
    api.getQuiz({ topic: t, limit: 15 })
      .then(setQuestions)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(topic); }, [topic, load]);

  const q = questions[idx];

  const toggle = (i) => {
    if (result || !q) return;
    if (q.type === 'single' || q.type === 'truefalse') {
      setChosen([i]);
    } else if (q.type === 'multi') {
      setChosen((prev) => {
        const arr = prev ?? [];
        return arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i];
      });
    }
  };

  const submit = async () => {
    if (!q || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.submitAttempt({ question_id: q.id, chosen: q.type === 'cloze' ? chosen : chosen ?? [] });
      setResult(r);
      setStats((s) => ({ correct: s.correct + (r.correct ? 1 : 0), total: s.total + 1 }));
    } finally { setSubmitting(false); }
  };

  const next = () => {
    if (idx + 1 >= questions.length) { load(topic); }
    else { setIdx((i) => i + 1); setChosen(null); setResult(null); }
  };

  const isSelected = (i) => Array.isArray(chosen) && chosen.includes(i);
  const pct = questions.length > 0 ? Math.round(((idx + 1) / questions.length) * 100) : 0;

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Nav & Filter */}
      <div className="flex items-center gap-3">
        <Link to="/lernen" className="text-sm text-ocean-600 hover:text-ocean-800 hover:underline transition-colors">← Zurück</Link>
        <select className="input text-sm ml-auto max-w-[180px]" aria-label="Thema filtern"
          value={topic} onChange={(e) => setTopic(e.target.value)}>
          {TOPIC_FILTER_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Stats — Dive-Computer-Stil */}
      {stats.total > 0 && (
        <div className="card-ocean flex gap-6 text-xs font-mono py-2.5 px-4">
          <span className="text-teal-600">▲ <span className="dive-value">{stats.correct}</span> richtig</span>
          <span className="text-coral-500">▼ <span className="dive-value">{stats.total - stats.correct}</span> falsch</span>
          <span className="text-ocean-500 ml-auto"><span className="dive-value">{Math.round(stats.correct / stats.total * 100)}</span> %</span>
        </div>
      )}

      {loading && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}

      {!loading && questions.length === 0 && (
        <div className="card text-center py-10 text-gray-500 text-sm">Keine Fragen gefunden.</div>
      )}

      {!loading && q && (
        <div className="space-y-4">
          {/* Tauchtiefe-Fortschrittsbalken */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-ocean-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #0ea5e9, #06b6d4)' }} />
            </div>
            <span className="text-xs font-mono text-ocean-500 shrink-0 dive-value">{idx + 1}/{questions.length}</span>
          </div>

          {/* Frage-Karte */}
          <div className="card border-ocean-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold uppercase tracking-widest text-ocean-500 bg-ocean-50 px-2 py-0.5 rounded-full">
                {topicLabel(q.topic)}
              </span>
              <span className="text-xs text-amber-400">{'★'.repeat(q.difficulty ?? 1)}{'☆'.repeat(3 - (q.difficulty ?? 1))}</span>
              <span className="ml-auto">
                <ReportButton type="question" refId={q.id} label={q.prompt} />
              </span>
            </div>
            <p className="font-medium text-gray-900 leading-relaxed">{q.prompt}</p>
          </div>

          {/* Antwortoptionen */}
          {(q.type === 'single' || q.type === 'multi' || q.type === 'truefalse') && (
            <div className="space-y-2">
              {q.options.map((opt, i) => {
                const sel  = isSelected(i);
                const corr = result?.answer?.includes(i);
                const wrong = result && sel && !corr;
                return (
                  <button key={i} onClick={() => toggle(i)}
                    className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-all ${
                      wrong  ? 'bg-red-50 border-red-400 text-red-800' :
                      corr   ? 'bg-teal-50 border-teal-400 text-teal-800' :
                      sel    ? 'bg-ocean-50 border-ocean-400 text-ocean-800' :
                      result ? 'bg-gray-50 border-gray-200 text-gray-400' :
                               'bg-white border-gray-200 hover:border-ocean-300 hover:bg-ocean-50 text-gray-800'
                    }`}
                  >
                    <span className="font-mono font-semibold mr-2 text-ocean-400">{String.fromCharCode(65 + i)}.</span>{opt}
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'cloze' && (
            <input className="input" placeholder="Antwort eingeben…"
              value={typeof chosen === 'string' ? chosen : ''}
              onChange={(e) => setChosen(e.target.value)}
              disabled={!!result} />
          )}

          {/* Feedback */}
          {result && (
            <div className={`rounded-xl p-4 text-sm border ${
              result.correct
                ? 'bg-teal-50 border-teal-200 text-teal-900'
                : 'bg-red-50 border-red-200 text-red-900'
            }`}>
              <p className="font-bold mb-1">{result.correct ? '🫧 Richtig!' : '⚠️ Leider falsch.'}</p>
              <p className="leading-relaxed">{result.explanation}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            {!result && (
              <button onClick={submit}
                disabled={submitting || chosen === null || (Array.isArray(chosen) && chosen.length === 0)}
                className="btn-primary flex-1">
                {submitting ? 'Prüfen…' : 'Antwort prüfen'}
              </button>
            )}
            {result && (
              <button onClick={next} className="btn-primary flex-1">
                {idx + 1 >= questions.length ? '🔄 Neue Runde' : 'Nächste Frage →'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
